import throttle from 'lodash.throttle';
import * as React from 'react';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';

import { transformSrtTracks } from 'srt-support-for-html5-videos';
import { TextButton } from '@oneaudi/unified-web-components';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import type { Video, VideoPlayerProps, Videos } from '../../../types';
import { FA_LOCATOR_ID } from '../../../types';
import { Context } from '../../context/Context';
import { logVideoPlayerComponentTrackingData } from '../../utils/debugging';
import parseAspectRatio from '../../utils/parse-aspect-ratio';
import BackgroundImageComponent from '../background-image/background-image';
import { useIsFirstRender } from '../hooks/useIsFirstRender';
import { useTracking } from '../hooks/useTracking';
import { useSeekingStatus } from '../hooks/video/useSeekingStatus';
import { useVideo } from '../hooks/video/useVideo';
import type { BreakpointLabel, Breakpoints } from '../../utils/breakpoints';
import { breakpoints } from '../../utils/breakpoints';
import { responsiveStyles } from '../../utils/responsiveStyles';
import { ReactComponent as StateActiveIcon } from '../../../icons/State-Active.svg';
import { ReactComponent as StateDefaultIcon } from '../../../icons/State-Default.svg';
import { useVideoOverlayVisibility } from '../hooks/useAudioDescOverlayVisibility';
import { useForceRestartWithAudio } from '../hooks/useForceRestartWithAudio';
import { useI18n } from '../../i18n';

interface WrapperProps {
  isCover?: boolean;
  videos: Videos;
}

const Wrapper = styled.div<WrapperProps>`
  ${({ isCover, videos }) =>
    isCover
      ? // Ignore layout tests:
        /* istanbul ignore next */
        css`
          height: 100%;
        `
      : css`
          background: #000;

          ${responsiveStyles({ 'padding-bottom': createStyles(videos) }, breakpoints)}

          overflow: hidden;
        `}
  position: relative;
  width: 100%;
`;

type StyledAdIconButtonProps = {
  showAdButton: boolean;
  isFullWidth?: boolean;
};
const StyledAdIconButton = styled(TextButton)<StyledAdIconButtonProps>`
  position: absolute;
  top: 0rem;
  z-index: 5;
  right: 10%;
  padding: 12px;
  opacity: ${({ showAdButton }) => (showAdButton ? '1' : '0')};

  transform: translateY(0);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  pointer-events: auto;

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    bottom: ${({ isFullWidth = true }) => (isFullWidth ? '15%' : '25%')};
    right: 1%;
    top: auto;
  }
`;

const createStyles = (videos: Videos): Partial<Breakpoints<string>> => {
  return Object.fromEntries(
    Object.entries(videos).map(([breakpoint, video]) => {
      const [x, y] = parseAspectRatio(video.aspectRatio);
      // swap sides for "padding-bottom trick"
      const aspectRatioPercentage = (y * 100) / x;

      return [breakpoint, `${aspectRatioPercentage}%`];
    }),
  );
};

interface VideoProps {
  isCover?: boolean;
}

const VideoComponent = styled.video<VideoProps>`
  height: 100%;
  ${({ isCover }): FlattenSimpleInterpolation =>
    !isCover &&
    css`
      left: 0;
      position: absolute;
      top: 0;
    `};
  object-fit: ${({ isCover }): string => {
    // Ignore layout tests:
    /* istanbul ignore next */
    return isCover ? 'cover' : 'contain';
  }};
  width: 100%;

  &::-webkit-media-controls-timeline {
    display: block;
  }
  // styling the subtitle
  &::cue {
    background: rgba(0, 0, 0, 0.6);
    font-size: 20px;
    font-family: AudiType, sans-serif;
    text-shadow: 1px 1px 2px black;
    opacity: 0.9;
  }
`;

/*
 * the hash at the end of the URL fixes a bug on Safari when the first
 * frame is not used as poster image when no custom poster image is defined
 */
const fixSrc = (poster: boolean, src: string) => {
  return poster ? src : `${src}#t=0.001`;
};

export const VideoPlayer: FC<VideoPlayerProps> = ({ videos, cover, playsInline, isFormatable }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const overlayRef = useRef(null);

  const isFirstRender = useIsFirstRender();

  const { videoService, localeService } = useContext(Context);
  /* In order to autoplay videos on modern browsers the should be initially muted */
  const [showReplayButton, setShowReplayButton] = useState(false);
  const [audioDescriptionEnabled, setAudioDescriptionEnabled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(videos.xs.autoPlay);
  const [isAtStart, setIsAtStart] = useState(true);
  const [wasStartedInitially, setWasStartedInitially] = useState(false);
  const overlayVisible = useVideoOverlayVisibility(videoRef.current, overlayRef);
  const forceRestartWithAudio = useForceRestartWithAudio(videoRef, audioRef);

  const isSeeking = useSeekingStatus(videoRef);
  const { videoProps } = useVideo(videoRef, videos.xs);
  const enableAudioDescriptionAriaLabel = useI18n('enableAudioDescriptionAriaLabel');
  const disableAudioDescriptionAriaLabel = useI18n('disableAudioDescriptionAriaLabel');

  const onEnded = useCallback(() => {
    setShowReplayButton(true);
    setIsPlaying(false);
  }, []);

  const onPlay = useCallback(() => {
    if (!wasStartedInitially) {
      setWasStartedInitially(true);
    }
    setShowReplayButton(false);
    setIsPlaying(true);
  }, []);

  const hasPosterImage = useMemo<boolean>(() => {
    return videos.xs.poster !== undefined;
  }, [videos]);

  useEffect(() => {
    videoService?.registerVideoRef(videoRef);
    return (): void => {
      videoService?.unregisterVideoRef();
    };
  }, [videoService]);

  useEffect(() => {
    const videoElement = videoRef.current;

    const updateTime = throttle((): void => {
      setIsAtStart(() => {
        return videoElement.currentTime <= 0.001;
      });

      tracking.videoPercentagePlayedEvent(videoElement.currentTime, videoElement.duration);
    }, 250);

    const transformSRT = async () => {
      await transformSrtTracks(videoElement);
    };

    const setIsNotPlaying = (): void => {
      setIsPlaying(false);
    };

    videoElement?.addEventListener('timeupdate', updateTime);
    videoElement?.addEventListener('pause', setIsNotPlaying);

    transformSRT().catch(() => {});

    return (): void => {
      videoElement?.removeEventListener('timeupdate', updateTime);
      videoElement?.removeEventListener('pause', setIsNotPlaying);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (!video || !audio) return undefined;

    if (!audioDescriptionEnabled && audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    if (audioDescriptionEnabled && !video.paused) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      forceRestartWithAudio();
    }
    const syncAudio = () => {
      if (!audioDescriptionEnabled || isSeeking) {
        audio.pause();
        return;
      }

      // If video has looped and audio has ended, reset audio to beginning
      if (video.currentTime < 0.5 && audio.ended) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
        return;
      }

      // Prevent trying to play audio beyond its length
      if (video.currentTime >= audio.duration) {
        if (!audio.paused) audio.pause();
        return;
      }

      // we need to ensure that the audio is always in sync with the video but
      // we cannot do it continually as this will make the audio laggy
      const drift = Math.abs(audio.currentTime - video.currentTime);
      if (drift > 0.3) {
        audio.currentTime = video.currentTime;
      }

      if (video.paused && !audio.paused) {
        audio.pause();
      } else if (!video.paused && audio.paused) {
        audio.play().catch(() => {});
      }
    };
    video.addEventListener('timeupdate', syncAudio);
    video.addEventListener('pause', () => {
      audio.pause();
    });
    video.addEventListener('play', () => {
      if (audioDescriptionEnabled) {
        audio.play().catch(() => {});
      }
    });

    return () => {
      video.removeEventListener('timeupdate', syncAudio);
    };
  }, [audioDescriptionEnabled, forceRestartWithAudio]);

  useEffect(() => {
    if (isAtStart && !isPlaying) {
      setShowReplayButton(false);
    }
  }, [isAtStart, isPlaying]);

  // Setup tracking START
  const tracking = useTracking(videoRef, {
    implementer: 37,
  });

  logVideoPlayerComponentTrackingData('tracking init started');

  useEffect(() => {
    if (!isFirstRender) {
      tracking.videoStartEvent();
    }
  }, [wasStartedInitially]);

  useEffect(() => {
    if (!isFirstRender) {
      if (isAtStart) {
        // skip this playing status change because of user has started the video
        return;
      }
      if (videoRef.current.currentTime >= videoRef.current.duration) {
        // skip this playing status change because video reached the end
        return;
      }
      if (videoRef.current.readyState === 1 || videoRef.current.seeking || isSeeking) {
        // skip this playing status change because video is seeking
        return;
      }

      // playing status must have been toggled by the user
      if (isPlaying) {
        tracking.videoResumeEvent();
      } else {
        tracking.videoPauseEvent();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    tracking.sendReadyEvent();
    tracking.registerImpressionTracking();

    logVideoPlayerComponentTrackingData('tracking init finished');
  }, []);
  // Setup tracking END

  return (
    <Wrapper
      isCover={cover}
      videos={videos}
      data-test-id={FA_LOCATOR_ID}
      role="group"
      aria-label={videos.xs.nativeControls && videos.xs.alt}
    >
      <VideoComponent
        autoPlay={videos.xs.autoPlay}
        isCover={cover}
        key={videos.xs.src}
        loop={videos.xs.loop}
        crossOrigin="anonymous"
        // In order to autoplay videos on modern browsers the should be initially muted
        muted={videos.xs.autoPlay || videos.xs.muted}
        controls={videos.xs.nativeControls}
        onEnded={onEnded}
        onPlay={onPlay}
        playsInline={playsInline}
        poster={videos.xs.nativeControls && hasPosterImage ? videos.xs.poster : ''}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        preload="metadata"
        // @ts-expect-error ref is fine
        ref={videoRef}
        {...videoProps}
      >
        {Object.entries(videos)
          .map(
            ([breakpoint, video]: [breakpoint: BreakpointLabel, video: Video]): [number, Video] => [
              breakpoints[breakpoint],
              video,
            ],
          )
          .sort(([a], [b]) => b - a)
          .map(([minWidth, { src }]) => (
            <source
              media={minWidth > 0 ? `(min-width:${minWidth}px)` : undefined}
              src={fixSrc(hasPosterImage, src)}
              key={src}
            />
          ))}
        {videos.xs.caption && (
          <track
            label={localeService.language.toUpperCase()}
            kind="captions"
            srcLang={localeService.language}
            src={videos.xs.caption}
          />
        )}
      </VideoComponent>
      {videos.xs.audioDescription && (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            ref={audioRef}
            src={videos.xs.audioDescription}
            hidden
            aria-hidden={!audioDescriptionEnabled}
            aria-label="Audio description track for the video"
            preload="auto"
          />

          <StyledAdIconButton
            ref={overlayRef}
            aria-label={
              audioDescriptionEnabled
                ? disableAudioDescriptionAriaLabel
                : enableAudioDescriptionAriaLabel
            }
            showAdButton={overlayVisible}
            isFullWidth={videos.xs.isFullWidth}
            iconPosition="leading"
            onClick={() => {
              setAudioDescriptionEnabled(!audioDescriptionEnabled);
            }}
            size="sm"
            variant="primary"
          >
            {audioDescriptionEnabled ? (
              <StateActiveIcon role="img" aria-hidden="true" />
            ) : (
              <StateDefaultIcon role="img" aria-hidden="true" />
            )}
          </StyledAdIconButton>
        </>
      )}

      {!videos.xs.nativeControls &&
        hasPosterImage &&
        !isPlaying &&
        !showReplayButton &&
        isAtStart && <BackgroundImageComponent {...{ isFormatable }} videos={videos} />}
    </Wrapper>
  );
};
